import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { PromoCodeProps } from "./types";
import { getInterval, getPrice } from "../../utils/common";
import {
  FIXED_DISCOUNT_PROMOTION,
  FOREVER_DURATION,
  FREE_TRIAL_PROMOTION,
  NOT_DEFINED_PROMOTION,
  SINGLE_USE_DURATION,
} from "../../services/api/subscription/constants";
import {
  IFixedDetail,
  IFreeDetail,
  IPirCentDetail,
} from "../../services/api/subscription/types";

import "./styles.scss";

export const PromoCodeField: FC<PromoCodeProps> = ({ promotion }) => {
  const { t } = useTranslation();

  const isFreeTrial = promotion.detail.kind === FREE_TRIAL_PROMOTION;

  const discount = useMemo(() => {
    if (promotion.detail.kind === NOT_DEFINED_PROMOTION) {
      return "";
    }

    if (promotion.detail.kind === FIXED_DISCOUNT_PROMOTION) {
      const { amount, currency } = (promotion.detail as IFixedDetail).amount;
      return getPrice(amount, currency);
    }

    if (isFreeTrial) {
      return t("freeTrial");
    }

    return `${(promotion.detail as IPirCentDetail).percent}%`;
  }, [promotion, t, isFreeTrial]);

  const duration = useMemo(() => {
    if (
      promotion.detail.duration === SINGLE_USE_DURATION ||
      promotion.detail.duration === FOREVER_DURATION ||
      promotion.detail.kind === NOT_DEFINED_PROMOTION
    ) {
      return null;
    }

    if (isFreeTrial) {
      return getInterval((promotion.detail as IFreeDetail).length);
    }

    // Should be fixed on backend. Duration is required https://docs.platform.capacit.org/dev/api-reference/platform/#promotiondetail_fixeddiscount.
    return (promotion.detail as IFreeDetail).duration
      ? getInterval((promotion.detail as IFreeDetail).duration)
      : null;
  }, [promotion, isFreeTrial]);

  const renderFreeTrial = () => {
    return (
      <div className="ams-promo_free">
        {duration ? (
          <>
            <div className="pr-2xs">{duration.length}</div>
            {t(duration.unit, { [duration.unit]: duration.length })}
          </>
        ) : null}
        <div className="pl-2xs">{discount}</div>
      </div>
    );
  };

  const renderDiscount = () => {
    return (
      <>
        {t("reduction", { discount: discount })}
        {duration ? (
          <>
            <span className="pl-2xs pr-2xs">{duration.length}</span>
            <span>
              {t(duration.unit, { [duration.unit]: duration.length })}
            </span>
          </>
        ) : null}
      </>
    );
  };

  return (
    <div className="ams-promo">
      <span className="ams-promo_name pr-2xs">{promotion.code}</span>

      {discount && !isFreeTrial && renderDiscount()}
      {discount && isFreeTrial && renderFreeTrial()}
    </div>
  );
};
